import * as React from 'react';
import {connect} from 'react-redux';
import {withStyles, WithStyles, IconButton, Dialog, DialogTitle} from '@material-ui/core';
import Button from '@components/common/button/Button';
import TranslateNew from '@translate/TranslateNew';
import {CalendarFABStyle} from './CalendarFAB.style';
import * as EventActions from '@services/events/event.action';
import TargetIcon from "@material-ui/icons/Adjust";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/EditRounded";
import CameraIcon from "@material-ui/icons/CameraAltOutlined";
import LibraryIcon from "@material-ui/icons/LibraryAdd";
import { IEvent, IAnalysedImage, AI_RESULT, EVENT_TYPE, IAiTask, AI_ANALYZE_TASK_TYPE, AI_TASK_TYPE } from '@app-types/websocket/event.types';
import CloseIcon from "@material-ui/icons/Close";
import * as UserActions from '@services/user/user.actions';
import AIOnboarding from './AIOnboarding.component'
import { DialogTop } from '@components/economy/DialogComponens';
import CordovaContext from 'src/cordovaContext';
import { fileToBase64, handleBlobUpload, handleFileUpload } from '@helpers/uploadHelper';
import { getPicture } from '@helpers/cameraHelper';
import * as FileActions from '@services/file/file.actions';
import { IUploadFile, IStuffBeingUploaded, IFinishedUpload } from '@app-types/websocket/file.types';
import { HELP_TOPICS } from '@constants/suggestionTypes';
import BottomBar from './BottomBar';
import { styleColors } from 'src/main.style';

const tn: TranslateNew = TranslateNew.getInstance();

// non-image types the AI analysis backend accepts; keep in sync with backend document support
const ANALYSABLE_DOC_EXTENSIONS = ["pdf","doc","docx","xls","xlsx","ppt","pptx","txt","csv","md","rtf"];
const FILE_INPUT_ACCEPT = "capture=camera,image/*," + ANALYSABLE_DOC_EXTENSIONS.map((ext) => "."+ext).join(",");

interface IProps extends WithStyles<typeof CalendarFABStyle> {
  userId:number;
  events:IEvent[];
  currentYear:number;
  currentMonth:number;
  inEditMode:boolean;
  selectedDay:Date; // the day marked in the calendar
  initialDay:Date; // first day in week view
  size:"small"|"medium"|"large";
  setSelectedDay : (year:number,month:number,date:number) => void;
  scrollToMonth : (year:number, month:number, smooth?:boolean) => void;
  seenEvent: (id:number) => void;
  createEventOnDate: () => void;
  createAiEvents: () => void;
  currentSchedules:number[];

  makeNewSuggestion: (scheduleId:number) => void;

  uploadFile: (file: IUploadFile) => void;
  uploadingFiles: IStuffBeingUploaded[];
  uploadedFiles: IFinishedUpload[];
  uploadedFileUsed: (fileId: number) => void;
  analyzeImage: (fileId: number) => void;
  dismissAnalyzedImage : (aiID:number) => void;
  dismissAiTask : (aiID:number) => void;
  dismissedAIOnboarding:boolean,
  dismissHelpNotice: (notice:string) => void;

  analyzedFiles: IAnalysedImage[];
  aiTasks: IAiTask[];
}



interface IState {
  hasBeenSentAsRead:number[];
  unseenEvents:IEvent[];
  fade:boolean;

  errorMessage:string;
  dataUrl:string|undefined;
  internalId:number[];
  showAiOnboarding:boolean;
  showAiInputs:boolean;
  hideFabs:boolean;

  redClicked:boolean;
}

class CalendarFAB extends React.Component<IProps, IState> {
  private fileRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

  constructor(props: IProps) {
    super(props);

    const unseenEvents = this.unseenEvents(props.events,[],props.userId);
    this.state = {
      hasBeenSentAsRead:[],
      unseenEvents,
      fade:true,
      showAiInputs:false,
      errorMessage:"",
      dataUrl:undefined,
      internalId:[],
      showAiOnboarding:false,
      hideFabs:false,
      redClicked:false,
    };
  }

  public componentDidUpdate(prevProps:IProps, oldState:IState) {
    if (this.props.events !== prevProps.events) {
      this.setState({unseenEvents:this.unseenEvents(this.props.events,this.state.hasBeenSentAsRead,this.props.userId)});
    }
    if (oldState.dataUrl !== this.state.dataUrl && this.state.dataUrl !== undefined) {
      const img = new Image();
      const data:string = this.state.dataUrl;

      img.onload = () => {

        let newImgWidth = img.width;
        let newImgHeight = img.height;
      
        if (img.width >= img.height && img.width > 1200) {
          newImgWidth = 1200;
          newImgHeight = Math.floor(((1200*img.height)/img.width));
        } else if (img.height > img.width && img.height > 1200) {
          newImgHeight = 1200;
          newImgWidth = Math.floor(((1200*img.width)/img.height));
        }

        setTimeout(() => {this.upload(img, newImgWidth, newImgHeight)}, 300); // upload image
      };
      img.onerror = (error) => {
        this.setState({errorMessage:"Error reading image"});
      };

      img.src = data;
    }
    if ((prevProps.uploadingFiles !== this.props.uploadingFiles || prevProps.uploadedFiles !== this.props.uploadingFiles)) {

      for (const f of this.props.uploadingFiles) {
        if (f.loaded === 100 && this.state.internalId.includes(f.internalId)) {

          const newInternal = this.state.internalId.filter((id) => id !== f.internalId);
          this.setState({internalId:newInternal, dataUrl:undefined});
          this.props.uploadedFileUsed(f.id);
          this.props.analyzeImage(f.id);
        }
      }
      for (const f of this.props.uploadedFiles) {
        if (this.state.internalId.includes(f.internalId)) {

          const newInternal = this.state.internalId.filter((id) => id !== f.internalId);
          this.setState({internalId:newInternal, dataUrl:undefined});
          this.props.uploadedFileUsed(f.id);
          this.props.analyzeImage(f.id);
        }
      }
      
    }
  }

  public upload(img: HTMLImageElement, width:number, height:number) {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    // don't paint a border here, as it is the resulting image
    const context = canvas.getContext('2d')!;
    context.save()

    context.globalCompositeOperation = 'destination-over';
    context.drawImage(img,0,0,width,height,);
    context.restore()
    canvas.toBlob((blob) => {
      if(blob){
        const fileUpload = handleBlobUpload(blob,"analyze.png",this.props.uploadFile);
        const internalIds = this.state.internalId.slice();
        internalIds.push(fileUpload.internalId);
        this.setState({internalId:internalIds});
      }
    })
  }

  public render() {
    const {classes, initialDay} = this.props;

    let purpleFabStyle:React.CSSProperties = {}
    let AiFabStyle:React.CSSProperties = {scale:0, pointerEvents:'none'};
    let AiFabClasses:string = classes.FAB+" "+classes.today;
    let redFabClesses:string = classes.FAB;
    let purpleFabContent = <><TargetIcon /><span>{tn.lang.today}</span></>;
    let purpleFabClickAction:(() => void)|undefined;
    let aiAction:(() => void)|undefined;
    let redFabStyle:React.CSSProperties = {}
    let redFabClickAction:(() => void)|undefined = () => this.setState({redClicked:!this.state.redClicked});

    const today = new Date();
    let aiContent:JSX.Element|string = "Ai";
    let redContent:JSX.Element = <AddIcon style={{fontSize:'42px'}}/>;

    let errorEventId:number = 0;
    let errorImportId:number = 0;

    if ((today.getMonth() === this.props.currentMonth && today.getFullYear() === this.props.currentYear 
      && (this.props.size !== "small" || (today.getDate() === initialDay.getDate() && today.getMonth() === initialDay.getMonth() 
      && today.getFullYear() === initialDay.getFullYear())))) {
      purpleFabStyle = {scale:0, pointerEvents:'none'}
    }
    if (this.props.inEditMode || this.state.hideFabs) {
      purpleFabStyle = {scale:0, pointerEvents:'none'}
      redFabStyle = {scale:0, pointerEvents:'none'}
    } else {

      if (this.props.currentSchedules.length === 0) {
        redFabClickAction = () => this.props.createEventOnDate();
      } else if (this.state.redClicked) {
        redContent = <div className={classes.FABcontent + " " + classes.aiSelectInner} style={{backgroundColor: 'transparent'}}>
          <div className={classes.aiSelectButton} onClick={() => {
            this.props.makeNewSuggestion(this.props.currentSchedules[0]);
            this.setState({redClicked:false}); }}>
            <EditIcon /><span className={classes.aiText}>{tn.lang.housing}</span>
          </div>
          <div className={classes.aiSelectButton} onClick={() => {this.props.createEventOnDate(); this.setState({redClicked:false});}}>
            <AddIcon /><span className={classes.aiText}>{tn.lang.activity}</span>
          </div>
        </div>
        redFabClickAction = undefined;
        redFabClesses = classes.FAB+" "+classes.aiSelectFAB 
      }

      if (this.state.unseenEvents.length > 0) {
        const firstEvent = this.state.unseenEvents[0];
        const eventDate = new Date(firstEvent.startTime);
        purpleFabContent = <div style={{width:'100%', height:'100%', display: 'flex', transition: 'opacity 0.2s', opacity:(this.state.fade?0:1)}}>
          <div onClick={() => this.seen(firstEvent, true)}
            style={{flex:'1', display: 'flex', flexDirection:'column', padding:'8px', alignItems: 'center', justifyContent: 'center'}}>
            <span style={{fontWeight: 200}}>{tn.lang.unseenEvent+" "+tn.formatDateMonthShort(eventDate)}</span>
            <span style={{fontSize: '16px', fontWeight: 300, marginTop: '8px'}}>
              {firstEvent.title}
            </span>
          </div>
          <IconButton onClick={() => this.seen(firstEvent, false)}>
            {/* Purple FAB bg is constant across themes — always actual white. */}
            <CloseIcon style={{color: '#FFFFFF'}}/>
          </IconButton>
        </div>;
        purpleFabStyle = {width: 'calc(100% - 122px)',borderRadius: '12px'};
      } else {
        purpleFabClickAction = () => this.props.setSelectedDay(today.getFullYear(), today.getMonth(), today.getDate());
        const isCordovaApp = this.context;
        if (purpleFabStyle.scale === 0) {

          let errorMessage:string = "";
          const isUploading:boolean = this.state.internalId.length > 0;
          let haveAIEventResult:boolean = false;
          let AiIsWorkingOnIt:boolean = false;
          

          for (const doneAnalysis of this.props.analyzedFiles) {
            if (doneAnalysis.result === AI_RESULT.IN_PROGRESS && doneAnalysis.dismissed !== true) {
              AiIsWorkingOnIt = true;
            } else if ((doneAnalysis.result === AI_RESULT.NO_DATA || doneAnalysis.result === AI_RESULT.FAIL) 
              && doneAnalysis.dismissed !== true) {
              errorEventId = doneAnalysis.id;
              if (doneAnalysis.errorMsg && doneAnalysis.errorMsg !== "") {
                errorMessage = doneAnalysis.errorMsg;
              } else {
                if (doneAnalysis.result === AI_RESULT.NO_DATA) {
                  errorMessage = "Could not find any events in submitted image...";
                } else {
                  errorMessage = "Failed to analyze image";
                }
              }
            } else if (doneAnalysis.result === AI_RESULT.SUCCESS && !doneAnalysis.dismissed && ((doneAnalysis.events 
              && doneAnalysis.events.length > 0)  || (doneAnalysis.markdown !== undefined && doneAnalysis.markdown.length > 0 ))) {
              haveAIEventResult = true;
            }
          }

          for (const aiTask of this.props.aiTasks) {
            if (aiTask.status === AI_RESULT.IN_PROGRESS && aiTask.dismissed !== true) {
              AiIsWorkingOnIt = true;
            } else if (aiTask.status === AI_RESULT.FAIL && aiTask.dismissed !== true && aiTask.id !== undefined) {
              errorImportId = aiTask.id;
              errorMessage = aiTask.errorMsg?aiTask.errorMsg:"Error fetching result";
            }
          }


          if (haveAIEventResult) { // result available
            aiAction = () => {this.props.createAiEvents();};
            AiFabStyle = {};
            AiFabClasses = AiFabClasses + " " + classes.aiReadyBackground;
            aiContent = <div className={classes.FABcontent}>
                <span className={classes.aiText}>Ai</span><span>{tn.lang.show}</span>
              </div>
  
          } else if (isUploading) { // analyzing
            aiAction = () => {return};
            AiFabStyle = {pointerEvents:'none'};
            AiFabClasses = AiFabClasses + " " + classes.aiAnalyzingBackground;
            aiContent = <div className={classes.FABcontent} style={{backgroundColor:styleColors.greyMedium3}}>
                <span className={classes.aiText}>Ai</span><span style={{fontSize:'8px'}}>{tn.lang.loading}</span>
              </div>
  
          } else if (AiIsWorkingOnIt) { // analyzing
            // aiAction = () => {this.setState({aiState:"HAVE_RESULT"})};
            aiAction = () => {return;};
            AiFabStyle = {pointerEvents:'none'};
            AiFabClasses = AiFabClasses + " " + classes.aiAnalyzingBackground;
            aiContent = <div className={classes.FABcontent} style={{backgroundColor:styleColors.greyMedium3}}>
              <span className={classes.aiText}>Ai</span><span style={{fontSize:'8px'}}>{tn.lang.analyzing}</span>
              </div>
  
          } else if (errorMessage !== "") { // analyze failed
            aiAction = () => {this.setState({ errorMessage })};
            AiFabStyle = {};
            AiFabClasses = AiFabClasses + " " + classes.aiFailedBackground;
            aiContent = <div className={classes.FABcontent} style={{}}>
                <span className={classes.aiText}>Ai</span><span style={{fontSize:'8px'}}>{tn.lang.error}</span>
              </div>
          } else if (this.state.showAiInputs) { // select input (only on cordova)
            AiFabStyle = {};
            AiFabClasses = AiFabClasses + " " + classes.aiSelectFAB;
            aiContent = <div className={classes.FABcontent + " " + classes.aiSelectInner}>
                <div className={classes.aiSelectButton} onClick={() => {this.loadImage("CAMERA"); this.setState({showAiInputs:false})}}>
                  <CameraIcon /><span className={classes.aiText}>{tn.lang.camera}</span>
                </div>
                <div className={classes.aiSelectButton} onClick={() => {if (this.fileRef.current) {this.fileRef.current.focus();}}}>
                  <LibraryIcon /><span className={classes.aiText}>{tn.lang.library}</span>
                  <input type="file" ref={this.fileRef} multiple={false} style = {{opacity:0, position: 'absolute', width:'80%'}} id={"add-file-direct"} 
                    onChange={this.updateLocalFile} accept={FILE_INPUT_ACCEPT} />
                </div>
              </div>
          } else if (isCordovaApp) { // ai is ready (cordova)
            aiAction = () => {this.setState({showAiInputs:true, showAiOnboarding:!this.props.dismissedAIOnboarding})};
            AiFabStyle = {};
            AiFabClasses = AiFabClasses + " " + classes.aiWaitBackground;
            aiContent = <div className={classes.FABcontent}>
                <span className={classes.aiText}>Ai</span>
              </div>
          } else if (!this.props.dismissedAIOnboarding) {
            aiAction = () => {this.setState({showAiOnboarding:true})};
            AiFabStyle = {};
            AiFabClasses = AiFabClasses + " " + classes.aiWaitBackground;
            aiContent = <div className={classes.FABcontent}>
                <span className={classes.aiText}>Ai</span>
              </div>
          } else { // ai is ready (NOT cordova)
            aiAction = () => {return;};
            AiFabStyle = {};
            AiFabClasses = AiFabClasses + " " + classes.aiWaitBackground;
            aiContent = <label htmlFor="add-file-direct" className={classes.FABcontent} 
              onClick={() => {if (this.fileRef.current) {this.fileRef.current.focus();}}}>
              <input type="file" ref={this.fileRef} multiple={false} style = {{opacity:0, position: 'absolute', width:'80%'}} id={"add-file-direct"} 
                onChange={this.updateLocalFile} accept={FILE_INPUT_ACCEPT} />
              <span className={classes.aiText}>Ai</span>
            </label>
          }
        }
      }
    }


    if (this.state.showAiInputs) {
      redFabStyle.zIndex = 98;
    } else if (this.state.redClicked) {
      purpleFabStyle.zIndex = 98;
    }

    return (<>
      <BottomBar hidden={this.props.inEditMode} displaySubmenyCallback={(isShown) => this.setState({hideFabs:isShown})}/>
      <div className={AiFabClasses} onClick={aiAction} style={AiFabStyle}>{aiContent}</div>
      <div className={classes.FAB+" "+classes.today} onClick={purpleFabClickAction} style={purpleFabStyle}>{purpleFabContent}</div>
      <div className={redFabClesses} style={redFabStyle} onClick={redFabClickAction}>{redContent}</div>
      <div style={{position: 'absolute',top: '0px',left: 0,width: '100%',height: '100%',opacity: 0, 
        zIndex: 99, pointerEvents:(this.state.showAiInputs || this.state.redClicked)?'auto':'none'}}
        onClick={() => this.setState({showAiInputs:false, redClicked:false})}
      />
      <Dialog // error message display
        open={this.state.errorMessage !== ""}
        onClose={() => {
          if(errorEventId !== 0) {this.props.dismissAnalyzedImage(errorEventId);}
          if(errorImportId !== 0) {this.props.dismissAiTask(errorImportId);} 
          this.setState({errorMessage:""})}}
      >
        <DialogTop>
          <DialogTitle>{tn.lang.error}</DialogTitle>
          <IconButton onClick={() => {
            if(errorEventId !== 0) {this.props.dismissAnalyzedImage(errorEventId);} 
            if(errorImportId !== 0) {this.props.dismissAiTask(errorImportId);} 
            this.setState({errorMessage:""})}}>
            <CloseIcon />
          </IconButton>
        </DialogTop>
        <div style={{padding: '8px 24px', display: 'flex', flexDirection: 'column'}}>
          {this.state.errorMessage}
        </div>
        <div style={{display: "flex", flexDirection: "column",alignItems: "center", margin: "8px 24px", padding: "8px 24px", gap: "10px"}} >
          <Button classes={{contained:classes.buttonContained}} variant='contained' style={{width: "fit-content"}} 
            onClick={() => {
              if(errorEventId !== 0) {this.props.dismissAnalyzedImage(errorEventId);} 
              if(errorImportId !== 0) {this.props.dismissAiTask(errorImportId);} 
              this.setState({errorMessage:""})}}>
            {tn.lang.close}
          </Button>
        </div>
      </Dialog>
      <Dialog // onboarding for AI
        open={this.state.showAiOnboarding}
        onClose={() => {this.setState({showAiOnboarding:false}); this.props.dismissHelpNotice(HELP_TOPICS.AI_EVENTS_ONBOARDING);}}
        classes={{paper:classes.fullscreenPaper}}
      >
        <div className={classes.background}>
          <div className={classes.circle}/>
        </div>
        <AIOnboarding close={() => {this.setState({showAiOnboarding:false}); this.props.dismissHelpNotice(HELP_TOPICS.AI_EVENTS_ONBOARDING);}}/>
      </Dialog> 
      </>
    );
  }

  private loadImage(source:"CAMERA") {
    getPicture(source, (data) => this.imageLoaded(data), (error) => this.imageFailed(error));
  }

  private imageLoaded(blob:Blob) {
    const fileUpload = handleBlobUpload(blob,"analyze.jpeg",this.props.uploadFile);
    const internalIds = this.state.internalId.slice();
    internalIds.push(fileUpload.internalId);
    this.setState({internalId:internalIds});
  }

  private imageFailed(error:string) {
    this.setState({errorMessage:error});
    console.log(error);
  }

  private seen(event:IEvent, scrollToEvent:boolean) {
    const hasBeenSentAsRead:number[] = [...this.state.hasBeenSentAsRead, event.id];
    const unseenEvents = this.unseenEvents(this.state.unseenEvents, hasBeenSentAsRead, this.props.userId);
    this.setState({fade:true,hasBeenSentAsRead, unseenEvents});
    if (scrollToEvent) {
      const eventDate = new Date(event.startTime);
      
      if (this.props.currentYear === eventDate.getFullYear() && this.props.currentYear === eventDate.getMonth()) {
        this.props.setSelectedDay(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate())
      } else {
        this.props.scrollToMonth(eventDate.getFullYear(), eventDate.getMonth(), true);
        setTimeout(() => this.props.setSelectedDay(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()), 400);
        setTimeout(() => {
          const element = document.getElementById("eventdetails-"+event.id);
          if (element !== null) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }  
          
        },600);
      }
    } else {
      this.props.seenEvent(event.id);
    }
  }

  private unseenEvents(events:IEvent[],markedEvents:number[],userId:number):IEvent[] {
    const twoMonthsBack = Date.now() - (1000*60*60*24*60);
    const unseenEvents:IEvent[] = [];
    for (const event of events) {
      if (event.creator === userId && event.eventType === EVENT_TYPE.NORMAL) {continue;}
      if (event.startTime < twoMonthsBack) {continue;}
      if (event.seen && event.seen.includes(userId)) {continue;}
      if (markedEvents.includes(event.id)) {continue;}
      unseenEvents.push(event);
    }
    unseenEvents.sort((a,b) => a.startTime - b.startTime);
    if (unseenEvents.length > 0) {
      setTimeout(() => this.setState({fade:false}), 200);
    }
    return unseenEvents;
  }

  private updateLocalFile = async (event: any) => {
    try {
      const { files } = event.target;
      if (files && files.length > 0 || (event.files && event.files.length > 0)) {
        const f:File = files.item(0) ? files.item(0) : event.files.item(0);
        if (f.type && f.type.toLowerCase().includes("image")) {
          const data = await fileToBase64(f);
          this.setState({dataUrl:  'data:'+f.type+';base64,'  + data});
        } else if (this.isAnalysableDocument(f.name)) {
          // documents skip the canvas resize and upload as they are
          const fileUpload = handleFileUpload(f, this.props.uploadFile);
          const internalIds = this.state.internalId.slice();
          internalIds.push(fileUpload.internalId);
          this.setState({internalId:internalIds});
        } else {
          this.setState({errorMessage:"Sorry we can't analyse that kind of data yet. "
            + "Please send us a chat message and tell us what you want to do!"});
        }
      }
    } catch (error) {
      console.error("Failed to read selected calendar image", error);
      this.setState({errorMessage:"Could not read the selected image. Please try another image."});
    }
  }

  private isAnalysableDocument(name:string|undefined):boolean {
    if (!name) {return false;}
    const ext = name.split('.').pop();
    return ext !== undefined && ANALYSABLE_DOC_EXTENSIONS.includes(ext.toLowerCase());
  }
}



function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;
  const dismissedHelpMessages = state.user.dismissedHelpNotices;
  let dismissedAIOnboarding = false;
  if (dismissedHelpMessages) {
    if (dismissedHelpMessages.includes(HELP_TOPICS.AI_EVENTS_ONBOARDING)) {
      dismissedAIOnboarding = true;
    }
  }

  const analyzedFiles:IAnalysedImage[] = (state.event.eventAnalyses?state.event.eventAnalyses:[])
    .filter((i: IAnalysedImage) => (i.taskType === AI_ANALYZE_TASK_TYPE.EVENT || i.taskType === AI_ANALYZE_TASK_TYPE.STRUCTURED));

  const aiTasks:IAiTask[] = (state.event.aiTasks
    ?state.event.aiTasks.filter((t:IAiTask) => (t.taskType === AI_TASK_TYPE.SCHOOL_VACATIONS))
    :[])
  return {
    userId,
    events:state.event.events?state.event.events:[],
    uploadingFiles: state.file.uploadSlots,
    uploadedFiles: state.file.uploadedFiles,
    analyzedFiles,
    aiTasks,
    dismissedAIOnboarding
  };
}

const mapDispatchToProps = {
  seenEvent: EventActions.seenEvent,
  uploadFile: FileActions.requestFileUpload,
  uploadedFileUsed: FileActions.uploadedFileUsed,
  analyzeImage : EventActions.analyzeImage,
  dismissAnalyzedImage : EventActions.dismissAnalyzedImage,
  dismissAiTask : EventActions.dismissAiTask,
  dismissHelpNotice:UserActions.dismissHelpNotice,
};

CalendarFAB.contextType = CordovaContext;

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(CalendarFABStyle)(CalendarFAB));



// WEBPACK FOOTER //
// ./src/components/calendar2/fab/CalendarFAB.tsx